import { useState, useRef, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Volume2, ArrowRight, Check } from "lucide-react";
import { pickFrenchVoice, devicePitch } from "@/lib/deviceVoice";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function speakFrench(text) {
  if (!window.speechSynthesis || !text) return null;
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.lang = "fr-FR";
  const fr = pickFrenchVoice();
  if (fr) u.voice = fr;
  u.rate = 0.85;
  u.pitch = devicePitch();
  return u;
}

export default function PronunciationPlayer({ game, onComplete, patientId }) {
  const words = (game.words || []).filter((w) => (w.text || "").trim());
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const start = useRef(Date.now());
  const genCache = useRef({});

  useEffect(() => () => { try { window.speechSynthesis?.cancel(); } catch { /* noop */ } }, []);

  const w = words[idx];

  const audioRef = useRef(null);

  const playUrl = (url) => {
    const full = url.startsWith("http") ? url : `${BACKEND}${url}`;
    const a = new Audio(full);
    audioRef.current = a;
    a.onended = () => setPlaying(false);
    a.onerror = () => setPlaying(false);
    setPlaying(true);
    a.play();
  };

  const play = useCallback(async () => {
    // 1) Per-patient generated voice (priority when in a resident session)
    if (patientId && w?.text) {
      if (genCache.current[idx]) { playUrl(genCache.current[idx]); return; }
      try {
        setPlaying(true);
        const r = await api.post(`/patients/${patientId}/tts`, { text: w.text });
        genCache.current[idx] = r.data.url;
        playUrl(r.data.url);
        return;
      } catch { setPlaying(false); /* fallback below */ }
    }
    // 2) Pre-generated word audio
    if (w?.audio_url && audioRef.current) {
      try {
        audioRef.current.currentTime = 0;
        audioRef.current.play();
        setPlaying(true);
        return;
      } catch { /* fallback */ }
    }
    // 3) Browser voice
    const u = speakFrench(w?.text);
    if (!u) return;
    u.onend = () => setPlaying(false);
    u.onerror = () => setPlaying(false);
    setPlaying(true);
    window.speechSynthesis.speak(u);
  }, [w, idx, patientId]);

  if (words.length === 0) {
    return <p className="text-center text-xl font-patient text-gray-500 mt-10">Cet exercice n'a pas encore de mots.</p>;
  }

  const total = words.length;

  const next = () => {
    try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
    try { audioRef.current?.pause(); } catch { /* noop */ }
    setPlaying(false);
    if (idx + 1 >= total) {
      const duration = Math.round((Date.now() - start.current) / 1000);
      onComplete({ score: 100, accuracy: 100, duration });
    } else {
      setIdx((i) => i + 1);
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto text-center" data-testid="pronunciation-player">
      <p className="text-lg font-care text-emerald-700 font-bold mb-4">Mot {idx + 1} / {total}</p>

      {w.image_url && (
        <img src={w.image_url} alt={w.text} className="w-56 h-56 object-cover rounded-3xl mx-auto mb-6 border-4 border-amber-200" data-testid="pronunciation-image" />
      )}

      <div className="rounded-3xl bg-white border-4 border-emerald-700/15 py-12 px-6 mb-8">
        <p className="font-patient font-black text-gray-800 text-6xl sm:text-7xl leading-tight" data-testid="pronunciation-word">{w.text}</p>
      </div>

      {w.audio_url && <audio ref={audioRef} src={w.audio_url} preload="none" onEnded={() => setPlaying(false)} onError={() => setPlaying(false)} />}

      <button onClick={play} data-testid="pronunciation-listen"
        className={`w-full min-h-[5rem] rounded-2xl font-patient font-black text-3xl flex items-center justify-center gap-4 active:scale-[0.98] transition-transform duration-150 text-white ${playing ? "bg-emerald-600" : "bg-emerald-700"}`}>
        <Volume2 className="w-10 h-10" /> {playing ? "Écoute…" : "Écouter"}
      </button>

      <p className="font-patient text-xl text-gray-500 mt-6 mb-8">Écoutez, puis répétez le mot à voix haute.</p>

      <button onClick={next} data-testid="pronunciation-next"
        className="w-full h-16 rounded-2xl bg-amber-400 text-amber-950 font-patient font-black text-2xl inline-flex items-center justify-center gap-3 active:scale-[0.98] transition-transform duration-150">
        {idx + 1 >= total ? <>Terminé <Check className="w-7 h-7" /></> : <>Mot suivant <ArrowRight className="w-7 h-7" /></>}
      </button>
    </div>
  );
}
